import React, { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Search01Icon, 
  InformationCircleIcon,
} from '@hugeicons/core-free-icons';
import Button from '../../components/Button';
import { api } from '../../utils/api';
import { FadeLoader } from 'react-spinners';
import ResponsiveModal from '../../components/ResponsiveModal';

const AgentClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  
  // Records Modal State
  const [isRecordsModalOpen, setIsRecordsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientTransactions, setClientTransactions] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  // Fetch Clients
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const clientsRes = await api.get(`/agent/clients?page=${page}&per_page=15&search=${searchTerm}`);
        if (clientsRes.data) {
          const clientsList = clientsRes.data.clients || clientsRes.data.data || [];
          const meta = clientsRes.data.pagination || { current_page: 1, last_page: 1, total: clientsList.length };
          setClients(clientsList);
          setPagination(meta);
        }
      } catch (err) {
        console.error('Failed to fetch agent data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); 
  };

  const handleViewRecords = async (client) => {
    setSelectedClient(client);
    setIsRecordsModalOpen(true);
    setRecordsLoading(true);
    try {
      const res = await api.get(`/agent/transactions?client_uid=${client.uid}&per_page=10`);
      setClientTransactions(res.data?.transactions || []);
    } catch (err) {
      console.error(err);
    } finally {
      setRecordsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-charcoal dark:text-white tracking-tight">Your Bound Clients</h1>
          <p className="text-muted-gray mt-1 font-medium">Manage and track your direct-to-agent relationships.</p>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 overflow-hidden shadow-sm">
        <div className="p-8 border-b border-light-gray/10 dark:border-dark-border/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <form onSubmit={handleSearch} className="relative w-full sm:w-96">
            <HugeiconsIcon icon={Search01Icon} size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-gray" />
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name..." 
              className="w-full pl-10 pr-4 py-2.5 bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl text-sm focus:ring-soft-green focus:border-soft-green outline-none text-charcoal dark:text-white"
            />
          </form>
          <div className="flex items-center gap-2 text-xs text-muted-gray font-bold">
            <HugeiconsIcon icon={InformationCircleIcon} size={14} />
            Total: {pagination.total} Clients
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-20 flex justify-center items-center">
              <FadeLoader color="#4ade80" />
            </div>
          ) : clients.length === 0 ? (
            <div className="p-20 text-center text-muted-gray">No clients found matching your criteria.</div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-off-white/30 dark:bg-gray-800/30 text-muted-gray text-[10px] uppercase font-black tracking-[0.2em]">
                <tr>
                  <th className="px-8 py-5">Client Profile</th>
                  <th className="px-8 py-5 text-center">Total Orders</th>
                  <th className="px-8 py-5 text-right">Lifetime Volume</th>
                  <th className="px-8 py-5 text-center">Linked Since</th>
                  <th className="px-8 py-5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-gray/10 dark:divide-dark-border/10">
                {clients.map((client) => (
                  <tr key={client.uid} className="hover:bg-soft-green/5 transition-colors group">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-soft-green/10 flex items-center justify-center text-soft-green font-black">
                          {(client.name || 'U')[0].toUpperCase()}
                        </div>
                        <span className="font-bold text-charcoal dark:text-gray-200">{client.name || 'Private User'}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center font-bold text-charcoal dark:text-gray-300">{client.total_orders || 0}</td>
                    <td className="px-8 py-6 text-right font-black text-charcoal dark:text-white">₦{parseFloat(client.total_transactions || 0).toLocaleString()}</td>
                    <td className="px-8 py-6 text-center text-xs text-muted-gray">{formatDate(client.linked_at)}</td>
                    <td className="px-8 py-6 text-right">
                      <button 
                        onClick={() => handleViewRecords(client)} 
                        className="text-xs font-black text-soft-green underline decoration-soft-green/30 underline-offset-4 hover:decoration-soft-green transition-all"
                      >
                        View Records
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Controls */}
        {pagination.last_page > 1 && (
          <div className="flex items-center justify-between px-8 py-6 border-t border-light-gray dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30">
            <span className="text-xs text-muted-gray font-medium">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-xs font-bold rounded-xl border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-white dark:hover:bg-gray-700 transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.last_page, p + 1))}
                disabled={page === pagination.last_page}
                className="px-4 py-2 text-xs font-bold rounded-xl border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-white dark:hover:bg-gray-700 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Client Records Modal */}
      <ResponsiveModal
        isOpen={isRecordsModalOpen}
        onClose={() => setIsRecordsModalOpen(false)}
        title={`History: ${selectedClient?.name || 'Client'}`}
      >
        <div className="py-4 space-y-4">
          {recordsLoading ? (
            <div className="py-10 flex justify-center"><FadeLoader color="#4ade80" /></div>
          ) : clientTransactions.length === 0 ? (
            <div className="py-10 text-center text-muted-gray text-sm">No transaction history found.</div>
          ) : (
            <div className="space-y-3">
              {clientTransactions.map(tx => (
                <div key={tx.uuid} className="p-4 bg-off-white dark:bg-gray-800 rounded-2xl flex justify-between items-center">
                  <div>
                    <p className="text-xs font-bold text-charcoal dark:text-white uppercase">
                      {tx.type === 'payment_confirmation' ? (tx.order?.transaction_type || 'deposit') : tx.type.replace('_', ' ')}
                    </p>
                    <p className="text-[10px] text-muted-gray">{new Date(tx.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-black text-soft-green">{tx.currency === 'NGN' ? '₦' : '$'}{parseFloat(tx.amount_sent).toLocaleString()}</p>
                    <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase ${
                      ['completed', 'verified'].includes(tx.status) ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400' :
                      ['failed', 'rejected', 'cancelled'].includes(tx.status) ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' :
                      tx.status === 'disputed' ? 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400' :
                      'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400'
                    }`}>
                      {tx.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button onClick={() => window.location.href = '/history'} variant="secondary" className="w-full py-4 rounded-2xl font-bold mt-4">
            View Full History
          </Button>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default AgentClients;
